import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Share2, Copy, Check, Star, Loader2, Users } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * "Share this view" + "Save as bookmark" button (iter376).
 *
 * Copies the current URL (search params baked in by the parent page's
 * filter state) to the clipboard so a teammate can click straight to
 * the same view. The bookmark popover lets the same URL be saved into
 * the user's personal "My Views" library (rendered on the dashboard).
 *
 * Pages opt-in by passing `params` (an object). Empty/null/undefined
 * values are stripped so we never copy a URL with `?branch=null`.
 *
 * Usage:
 *   <ShareViewButton params={{ period: '90d', branch: branchId, q: search }} />
 */
export default function ShareViewButton({ params = {}, label = 'Share this view', size = 'sm', variant = 'outline', className = '', showBookmark = true, defaultBookmarkName = '' }) {
  const [copied, setCopied] = useState(false);
  const [savingBookmark, setSavingBookmark] = useState(false);
  const [bookmarkName, setBookmarkName] = useState(defaultBookmarkName);
  const [bookmarkOpen, setBookmarkOpen] = useState(false);
  // iter377 — tags + share-with-team
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState([]);
  const [shareWithTeam, setShareWithTeam] = useState(false);

  const addTag = () => {
    const v = tagInput.trim().toLowerCase();
    if (!v) return;
    if (tags.length >= 8) { toast.error('Max 8 tags per bookmark'); return; }
    if (!tags.includes(v)) setTags(prev => [...prev, v]);
    setTagInput('');
  };
  const removeTag = (t) => setTags(prev => prev.filter(x => x !== t));

  const buildUrl = () => {
    const usp = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '' || v === 'all') return;
      usp.set(k, String(v));
    });
    const qs = usp.toString();
    const { origin, pathname } = window.location;
    return qs ? `${origin}${pathname}?${qs}` : `${origin}${pathname}`;
  };

  const buildRelativeUrl = () => {
    const url = buildUrl();
    try {
      const u = new URL(url);
      return u.pathname + u.search;
    } catch {
      return url;
    }
  };

  const onShare = async () => {
    const url = buildUrl();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied — paste it anywhere');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.info('Copy this link manually:', { description: url, duration: 8000 });
    }
  };

  const onSaveBookmark = async () => {
    const name = bookmarkName.trim();
    if (!name) { toast.error('Give this view a name first'); return; }
    setSavingBookmark(true);
    try {
      await api.post('/me/bookmarks', {
        name,
        url: buildRelativeUrl(),
        tags,
        scope: shareWithTeam ? 'tenant' : 'private',
      });
      toast.success(
        shareWithTeam
          ? `Saved & shared with team: "${name}"`
          : `Saved "${name}" — find it in My Views on the dashboard`,
      );
      setBookmarkOpen(false);
      setBookmarkName('');
      setTags([]);
      setShareWithTeam(false);
      window.dispatchEvent(new CustomEvent('bookmarks:changed'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save bookmark');
    } finally {
      setSavingBookmark(false);
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <Button
        type="button"
        size={size}
        variant={variant}
        onClick={onShare}
        className={`rounded-xl text-xs gap-1.5 ${className}`}
        data-testid="share-view-btn"
        title="Copy a link to this view"
      >
        {copied ? <Check size={13} className="text-emerald-600" /> : <Share2 size={13} />}
        <span>{copied ? 'Copied!' : label}</span>
        {!copied && <Copy size={11} className="opacity-50" />}
      </Button>
      {showBookmark && (
        <Popover open={bookmarkOpen} onOpenChange={setBookmarkOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              size={size}
              variant="ghost"
              className="rounded-xl px-2"
              title="Save this view as a bookmark"
              data-testid="bookmark-view-btn"
            >
              <Star size={13} className="text-amber-500" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="end" data-testid="bookmark-popover">
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium">Save this view</p>
                <p className="text-xs text-muted-foreground">
                  Saved views appear in <strong>My Views</strong> on the dashboard.
                </p>
              </div>
              <div>
                <Label htmlFor="bk-name" className="text-xs mb-1">Name *</Label>
                <Input
                  id="bk-name"
                  value={bookmarkName}
                  onChange={(e) => setBookmarkName(e.target.value)}
                  placeholder="e.g. Monday Cash & Bank"
                  autoFocus
                  data-testid="bookmark-name-input"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) onSaveBookmark(); }}
                />
              </div>

              <div>
                <Label htmlFor="bk-tags" className="text-xs mb-1">Tags (optional)</Label>
                <div className="flex gap-1.5 flex-wrap mb-1.5" data-testid="bookmark-tags-list">
                  {tags.map(t => (
                    <Badge
                      key={t}
                      variant="secondary"
                      className="cursor-pointer hover:bg-rose-100 text-[10px]"
                      onClick={() => removeTag(t)}
                      data-testid={`bookmark-tag-${t}`}
                    >
                      {t} ×
                    </Badge>
                  ))}
                </div>
                <Input
                  id="bk-tags"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Type a tag, press Enter"
                  data-testid="bookmark-tag-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  onBlur={addTag}
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Tags group bookmarks into folders on the dashboard. Up to 8 per view.
                </p>
              </div>

              <div className="flex items-center justify-between pt-1 border-t">
                <div className="flex gap-2 items-center">
                  <Users size={13} className="text-violet-500" />
                  <Label htmlFor="bk-share" className="text-xs cursor-pointer">Share with my team</Label>
                </div>
                <Switch
                  id="bk-share"
                  checked={shareWithTeam}
                  onCheckedChange={setShareWithTeam}
                  data-testid="bookmark-share-toggle"
                />
              </div>
              {shareWithTeam && (
                <p className="text-[11px] text-violet-700 -mt-1">
                  Everyone in your tenant will see this in <strong>Team Views</strong>.
                </p>
              )}

              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" className="rounded-xl text-xs" onClick={() => setBookmarkOpen(false)}>
                  Cancel
                </Button>
                <Button size="sm" className="rounded-xl text-xs bg-amber-500 hover:bg-amber-600 text-white" onClick={onSaveBookmark} disabled={savingBookmark} data-testid="bookmark-save-btn">
                  {savingBookmark ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Star size={12} className="mr-1" />}
                  Save
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
